<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t('discount_campaign_list') }}</h4>
        </div>
      </div>
      <div class="page-btn">
        <router-link
          v-if="can('inventory.discount_campaign', 'W')"
          to="/discount/campaigns/create"
          class="btn btn-added"
        >
          <i class="ti ti-circle-plus me-2 text-white"></i>{{ $t('add_discount_campaign') }}
        </router-link>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-body py-3 d-flex flex-wrap gap-3 align-items-end">
        <div>
          <label class="form-label mb-1">{{ $t('date_from') }}</label>
          <input v-model="dateFrom" type="date" class="form-control form-control-sm" @change="reloadTable" />
        </div>
        <div>
          <label class="form-label mb-1">{{ $t('date_to') }}</label>
          <input v-model="dateTo" type="date" class="form-control form-control-sm" @change="reloadTable" />
        </div>
      </div>
    </div>

    <ThemeDataTable
      ref="campaignTable"
      table-id="DiscountCampaignTable"
      :fetch-data="fetchCampaignsPage"
      :filters="tableFilters"
      :default-order-column="0"
      default-order-dir="desc"
    >
      <template #default="{ rows }">
        <thead class="thead-light">
          <tr>
            <th>{{ $t('sl') }}</th>
            <th>{{ $t('campaign_name') }}</th>
            <th>{{ $t('discount_type') }}</th>
            <th>{{ $t('discount_value') }}</th>
            <th>{{ $t('number_of_items') }}</th>
            <th>{{ $t('start_date_time') }}</th>
            <th>{{ $t('end_date_time') }}</th>
            <th>{{ $t('status') }}</th>
            <th>{{ $t('created_by') }}</th>
            <th class="no-sort text-end">{{ $t('action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in rows" :key="row.id">
            <td>{{ row._sl || idx + 1 }}</td>
            <td>{{ row.name }}</td>
            <td>{{ row.discount_type_display || row.discount_type }}</td>
            <td>{{ formatDiscountValue(row) }}</td>
            <td>{{ row.item_count || 0 }}</td>
            <td>{{ formatDateTime(row.start_at) }}</td>
            <td>{{ formatDateTime(row.end_at) }}</td>
            <td>
              <span class="badge" :class="statusClass(row.effective_status)">
                {{ statusLabel(row.effective_status) }}
              </span>
            </td>
            <td>{{ row.created_by_name || '—' }}</td>
            <td class="action-table-data text-end text-nowrap">
              <div class="d-flex justify-content-end gap-1 flex-wrap">
                <router-link
                  :to="{ name: 'DiscountCampaignDetail', params: { id: row.id } }"
                  class="btn btn-sm btn-outline-secondary"
                  :title="$t('view')"
                >
                  <i class="ti ti-eye"></i>
                </router-link>
                <router-link
                  v-if="canEdit(row)"
                  :to="{ name: 'DiscountCampaignEdit', params: { id: row.id } }"
                  class="btn btn-sm btn-outline-primary"
                  :title="$t('edit')"
                >
                  <i class="ti ti-edit"></i>
                </router-link>
                <button
                  v-if="canActivate(row)"
                  type="button"
                  class="btn btn-sm btn-success"
                  :disabled="actingId === row.id"
                  @click="doAction(row, 'activate')"
                >
                  {{ $t('activate') }}
                </button>
                <button
                  v-if="canPause(row)"
                  type="button"
                  class="btn btn-sm btn-warning"
                  :disabled="actingId === row.id"
                  @click="doAction(row, 'pause')"
                >
                  {{ $t('pause') }}
                </button>
                <button
                  v-if="canCancel(row)"
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  :disabled="actingId === row.id"
                  @click="doAction(row, 'cancel')"
                >
                  {{ $t('cancel') }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </ThemeDataTable>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { can as canAccess } from '@/helpers/permission';
import apiUrls from '@/urls';
import ThemeDataTable from '@/components/common/ThemeDataTable.vue';

export default {
  name: 'DiscountCampaignList',
  components: { ThemeDataTable },
  data() {
    return {
      actingId: null,
      toast: useToast(),
      dateFrom: '',
      dateTo: '',
      filterOptions: {
        status: ['draft', 'scheduled', 'active', 'paused', 'expired', 'cancelled'],
      },
    };
  },
  computed: {
    tableFilters() {
      return [
        {
          key: 'status',
          label: this.$t('status'),
          options: this.filterOptions.status,
        },
      ];
    },
  },
  methods: {
    reloadTable() {
      this.$refs.campaignTable?.reload?.();
    },
    can(code, level) {
      return canAccess(code, level);
    },
    statusLabel(status) {
      const map = {
        draft: this.$t('status_draft'),
        scheduled: this.$t('status_scheduled'),
        active: this.$t('status_active'),
        paused: this.$t('status_paused'),
        expired: this.$t('status_expired'),
        cancelled: this.$t('status_cancelled'),
      };
      return map[status] || status;
    },
    statusClass(status) {
      return {
        draft: 'bg-secondary',
        scheduled: 'bg-info',
        active: 'bg-success',
        paused: 'bg-warning text-dark',
        expired: 'bg-dark',
        cancelled: 'bg-danger',
      }[status] || 'bg-secondary';
    },
    formatDateTime(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString();
    },
    formatDiscountValue(row) {
      const v = Number(row.discount_value || 0);
      if (row.discount_type === 'percentage') return `${v}%`;
      return `৳${v.toFixed(2)}`;
    },
    canEdit(row) {
      return this.can('inventory.discount_campaign', 'E')
        && !['cancelled', 'expired'].includes(row.effective_status);
    },
    canActivate(row) {
      return this.can('inventory.discount_campaign', 'W')
        && ['draft', 'paused', 'scheduled'].includes(row.effective_status);
    },
    canPause(row) {
      return this.can('inventory.discount_campaign', 'W')
        && ['active', 'scheduled'].includes(row.effective_status);
    },
    canCancel(row) {
      return this.can('inventory.discount_campaign', 'W')
        && !['cancelled', 'expired'].includes(row.effective_status);
    },
    async fetchCampaignsPage({ draw, start, length, search, orderColumn, orderDir, filters }) {
      const params = {
        draw,
        start,
        length,
        'search[value]': search || '',
        'order[0][column]': orderColumn,
        'order[0][dir]': orderDir,
      };
      if (filters?.status) params.status = filters.status;
      if (this.dateFrom) params.date_from = this.dateFrom;
      if (this.dateTo) params.date_to = this.dateTo;

      const { data } = await this.$axios.get(apiUrls.discount_campaign.get, { params });
      const rows = (data.data || []).map((row, i) => ({
        ...row,
        _sl: start + i + 1,
      }));
      return {
        draw: data.draw,
        recordsTotal: data.recordsTotal,
        recordsFiltered: data.recordsFiltered,
        data: rows,
      };
    },
    async doAction(row, action) {
      const messages = {
        activate: this.$t('confirm_activate_campaign'),
        pause: this.$t('confirm_pause_campaign'),
        cancel: this.$t('confirm_cancel_campaign'),
      };
      if (!window.confirm(messages[action])) return;
      this.actingId = row.id;
      try {
        const url = apiUrls.discount_campaign[action](row.id);
        await this.$axios.post(url);
        this.toast.success(this.$t('campaign_updated'));
        this.$refs.campaignTable?.reload?.();
      } catch (err) {
        const detail = err?.response?.data?.detail || this.$t('something_went_wrong');
        this.toast.error(detail);
      } finally {
        this.actingId = null;
      }
    },
  },
};
</script>
